import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { ReactNode } from "react";
import Chip from "components/Environment/Create/chips";
import { classNames } from "components/utils";

interface LaunchableInteractiveProps {
  isSectionInfoEnabled: boolean;
  isAnimationEnabled: boolean;
}

const LaunchableInteractive: React.FC<LaunchableInteractiveProps> = ({
  isSectionInfoEnabled,
  isAnimationEnabled,
}) => {
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [sequenceIndex, setSequenceIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const [scale, setScale] = useState<number>(1.6);

  // Define grid parameters
  const gridWidth = 6; // 6 cells wide
  const gridHeight = 8; // 8 cells high
  const baseWidth = 580;
  const baseHeight = 150;
  const baseCellSize = 12; // Smaller cell size to fit in the new dimensions

  // Apply scaling to cell size only, not to overall dimensions
  const cellSize = baseCellSize * scale;
  const width = baseWidth; // Fixed width
  const height = baseHeight; // Fixed height

  // NVIDIA brand colors
  const nvidiaColors = {
    green: "#76B900", // NVIDIA primary green
    darkGreen: "#57A100", // Darker green for hover
    black: "#1A1A1A", // NVIDIA black
    darkGray: "#2D2D2D", // Dark gray for backgrounds
    lightGray: "#CCCCCC", // Light gray for text and accents
    white: "#FFFFFF", // White
  };

  // Add a new interface for section overlay configuration after the nvidiaColors definition
  interface SectionOverlay {
    component: ReactNode;
    width?: number; // Relative width (0-1)
    height?: number; // Relative height (0-1)
    offsetX?: number; // Horizontal offset in pixels
    offsetY?: number; // Vertical offset in pixels
    rotation?: number; // Additional rotation in degrees
    scale?: number; // Additional scaling factor
  }

  // Isometric projection helpers with centering adjustment for scaling
  const isoX = (x: number, y: number) => width / 2 + (x - y) * cellSize;
  const isoY = (x: number, y: number) => 12 + ((x + y) * cellSize) / 2;

  // Define the GPU Compute rectangle (4x6) - centered in the 6x8 area
  const computeRect = {
    x: 1, // Positioned within the 6x8 grid
    y: 1,
    width: 4,
    height: 6,
  };

  // Calculate the corners of the compute rectangle
  const computeTopLeft = {
    x: isoX(computeRect.x, computeRect.y),
    y: isoY(computeRect.x, computeRect.y),
  };
  const computeTopRight = {
    x: isoX(computeRect.x + computeRect.width, computeRect.y),
    y: isoY(computeRect.x + computeRect.width, computeRect.y),
  };
  const computeBottomRight = {
    x: isoX(
      computeRect.x + computeRect.width,
      computeRect.y + computeRect.height
    ),
    y: isoY(
      computeRect.x + computeRect.width,
      computeRect.y + computeRect.height
    ),
  };
  const computeBottomLeft = {
    x: isoX(computeRect.x, computeRect.y + computeRect.height),
    y: isoY(computeRect.x, computeRect.y + computeRect.height),
  };

  // Define the container square (top 2x2 section)
  const containerSquare = {
    x: computeRect.x + 1, // 1 cell from the left edge of compute
    y: computeRect.y + 1, // 1 cell from the top edge of compute
    width: 2,
    height: 2,
  };

  // Calculate the corners of the container square
  const containerTopLeft = {
    x: isoX(containerSquare.x, containerSquare.y),
    y: isoY(containerSquare.x, containerSquare.y),
  };
  const containerTopRight = {
    x: isoX(containerSquare.x + containerSquare.width, containerSquare.y),
    y: isoY(containerSquare.x + containerSquare.width, containerSquare.y),
  };
  const containerBottomRight = {
    x: isoX(
      containerSquare.x + containerSquare.width,
      containerSquare.y + containerSquare.height
    ),
    y: isoY(
      containerSquare.x + containerSquare.width,
      containerSquare.y + containerSquare.height
    ),
  };
  const containerBottomLeft = {
    x: isoX(containerSquare.x, containerSquare.y + containerSquare.height),
    y: isoY(containerSquare.x, containerSquare.y + containerSquare.height),
  };

  // Define the git resources square (bottom 2x2 section) - directly below container
  const gitSquare = {
    x: containerSquare.x, // Same x as container
    y: containerSquare.y + containerSquare.height, // Directly below container (no gap)
    width: 2,
    height: 2,
  };

  // Calculate the corners of the git square
  const gitTopLeft = {
    x: isoX(gitSquare.x, gitSquare.y),
    y: isoY(gitSquare.x, gitSquare.y),
  };
  const gitTopRight = {
    x: isoX(gitSquare.x + gitSquare.width, gitSquare.y),
    y: isoY(gitSquare.x + gitSquare.width, gitSquare.y),
  };
  const gitBottomRight = {
    x: isoX(gitSquare.x + gitSquare.width, gitSquare.y + gitSquare.height),
    y: isoY(gitSquare.x + gitSquare.width, gitSquare.y + gitSquare.height),
  };
  const gitBottomLeft = {
    x: isoX(gitSquare.x, gitSquare.y + gitSquare.height),
    y: isoY(gitSquare.x, gitSquare.y + gitSquare.height),
  };

  // Define the sections with NVIDIA colors
  const sections = [
    {
      id: "compute",
      name: "GPU Compute",
      color: "#000",
      hoverColor: "#0C0C0C",
      type: "outer",
      points: `${computeTopLeft.x},${computeTopLeft.y} ${computeTopRight.x},${computeTopRight.y} ${computeBottomRight.x},${computeBottomRight.y} ${computeBottomLeft.x},${computeBottomLeft.y}`,
      overlay: {
        component: (
          <Chip
            selected={false}
            chipName={"H100"}
            manufacturerName={"NVIDIA"}
            onClick={() => {}}
            className={classNames("h-[80px]")}
          />
        ),
        width: 2,
        height: 2,
        offsetX: 80,
        offsetY: 30,
        rotation: 0,
        scale: 1,
        isForeignObject: true,
      },
    },
    {
      id: "containers",
      name: "Containers",
      color: "#007ACC",
      hoverColor: "#0062A3",
      type: "inner-top",
      points: `${containerTopLeft.x},${containerTopLeft.y} ${containerTopRight.x},${containerTopRight.y} ${containerBottomRight.x},${containerBottomRight.y} ${containerBottomLeft.x},${containerBottomLeft.y}`,
      overlay: {
        component: (
          <g>
            <rect
              width="512"
              height="512"
              rx="15%"
              fill="#ffffff"
              fillOpacity="0.9"
            />
            <path
              stroke="#066da5"
              strokeWidth="38"
              d="M296 226h42m-92 0h42m-91 0h42m-91 0h41m-91 0h42m8-46h41m8 0h42m7 0h42m-42-46h42"
            />
            <path
              fill="#066da5"
              d="m472 228s-18-17-55-11c-4-29-35-46-35-46s-29 35-8 74c-6 3-16 7-31 7H68c-5 19-5 145 133 145 99 0 173-46 208-130 52 4 63-39 63-39"
            />
          </g>
        ),
        width: 0.75,
        height: 0.75,
        offsetX: -9,
        offsetY: 5,
        rotation: 0,
        scale: 1,
      },
    },
    {
      id: "git",
      name: "Git Resources",
      color: "#9e9e9e",
      hoverColor: "#b3b3b3",
      type: "inner-bottom",
      points: `${gitTopLeft.x},${gitTopLeft.y} ${gitTopRight.x},${gitTopRight.y} ${gitBottomRight.x},${gitBottomRight.y} ${gitBottomLeft.x},${gitBottomLeft.y}`,
      overlay: {
        component: (
          <g>
            <rect
              width="512"
              height="512"
              rx="15%"
              fill="#f0f0f0"
              fillOpacity="0.9"
            />
            <path
              fill="#f05033"
              d="M440.8 238.9L273 71.2c-9.7-9.7-25.3-9.7-35 0l-35 35 44.2 44.2c10.3-3.5 22.2-1.2 30.4 7 8.3 8.3 10.6 20.4 7 30.8l42.6 42.6c10.4-3.6 22.5-1.3 30.8 7 11.6 11.6 11.6 30.3 0 41.9-11.6 11.6-30.3 11.6-41.9 0-8.7-8.7-10.9-21.5-6.5-32.2l-39.7-39.7v104.5c2.8 1.4 5.5 3.3 7.9 5.7 11.6 11.6 11.6 30.3 0 41.9-11.6 11.6-30.3 11.6-41.9 0-11.6-11.6-11.6-30.3 0-41.9 2.9-2.9 6.2-5 9.7-6.4v-105.4c-3.5-1.4-6.8-3.5-9.7-6.4-8.8-8.8-10.9-21.7-6.4-32.5l-43.5-43.5-115.3 115.3c-9.7 9.7-9.7 25.3 0 35l167.8 167.8c9.7 9.7 25.3 9.7 35 0l167.2-167.2c9.7-9.7 9.7-25.3 0-35z"
            />
          </g>
        ),
        width: 0.75,
        height: 0.75,
        offsetX: -10,
        offsetY: 5,
        rotation: 0,
        scale: 1,
      },
    },
    {
      id: "networking",
      name: "Networking",
      color: nvidiaColors.green,
      hoverColor: nvidiaColors.darkGreen,
      type: "edge",
    },
  ];

  // Get all non-edge sections for the sequence
  const sequenceSections = sections.filter(
    (section) => section.type !== "edge"
  );

  useEffect(() => {
    if (!isAnimationEnabled || isPaused) {
      return;
    }

    const interval = setInterval(() => {
      setSequenceIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % (sequenceSections.length + 1);
        // If we've reached the end (nextIndex is equal to length), clear active section
        if (nextIndex === sequenceSections.length) {
          setActiveSection(null);
          return nextIndex;
        }
        // Otherwise, set the next section as active
        setActiveSection(sequenceSections[nextIndex].id);
        return nextIndex;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isAnimationEnabled, sequenceSections, isPaused]);

  // Generate grid lines
  const gridLines: React.ReactNode[] = [];
  const gridColor = "#5e5e5e";

  // Horizontal grid lines
  for (let y = 0; y <= gridHeight; y++) {
    for (let x = 0; x < gridWidth; x++) {
      gridLines.push(
        <line
          key={`h-${x}-${y}`}
          x1={isoX(x, y)}
          y1={isoY(x, y)}
          x2={isoX(x + 1, y)}
          y2={isoY(x + 1, y)}
          stroke={gridColor}
          strokeWidth="0.5"
          strokeOpacity="0.4"
        />
      );
    }
  }

  // Vertical grid lines
  for (let x = 0; x <= gridWidth; x++) {
    for (let y = 0; y < gridHeight; y++) {
      gridLines.push(
        <line
          key={`v-${x}-${y}`}
          x1={isoX(x, y)}
          y1={isoY(x, y)}
          x2={isoX(x, y + 1)}
          y2={isoY(x, y + 1)}
          stroke={gridColor}
          strokeWidth="0.5"
          strokeOpacity="0.4"
        />
      );
    }
  }

  // Generate static networking pulse lines
  const networkingLines: React.ReactNode[] = [];

  // Define static lines from grid edge to compute rectangle
  const staticNetworkingLines = [
    // Top edge to top of compute
    {
      startX: 2,
      startY: 0,
      endX: computeRect.x + 1,
      endY: computeRect.y,
      delay: 0.2,
      dashArray: "3,3",
    },
    {
      startX: 4,
      startY: 0,
      endX: computeRect.x + 3,
      endY: computeRect.y,
      delay: 0.5,
      dashArray: "2,4",
    },
    // Right edge to right of compute
    {
      startX: gridWidth,
      startY: 3,
      endX: computeRect.x + computeRect.width,
      endY: computeRect.y + 2,
      delay: 0.3,
      dashArray: "4,2",
    },
    {
      startX: gridWidth,
      startY: 5,
      endX: computeRect.x + computeRect.width,
      endY: computeRect.y + 4,
      delay: 0.6,
      dashArray: "3,3",
    },
    // Bottom edge to bottom of compute
    {
      startX: 2,
      startY: gridHeight,
      endX: computeRect.x + 1,
      endY: computeRect.y + computeRect.height,
      delay: 0.7,
      dashArray: "3,3",
    },
    {
      startX: 4,
      startY: gridHeight,
      endX: computeRect.x + 3,
      endY: computeRect.y + computeRect.height,
      delay: 0.1,
      dashArray: "2,3",
    },
    // Left edge to left of compute
    {
      startX: 0,
      startY: 3,
      endX: computeRect.x,
      endY: computeRect.y + 2,
      delay: 0.4,
      dashArray: "3,2",
    },
    {
      startX: 0,
      startY: 5,
      endX: computeRect.x,
      endY: computeRect.y + 4,
      delay: 0.8,
      dashArray: "2,2",
    },
  ];

  // Create the static networking lines with hover capability
  staticNetworkingLines.forEach((line, i) => {
    // Create an invisible wider line for better hover detection
    networkingLines.push(
      <line
        key={`net-line-hover-${i}`}
        x1={isoX(line.startX, line.startY)}
        y1={isoY(line.startX, line.startY)}
        x2={isoX(line.endX, line.endY)}
        y2={isoY(line.endX, line.endY)}
        stroke="transparent"
        strokeWidth="10" // Wider invisible stroke for easier hovering
        onMouseEnter={() => setHoveredSection("networking")}
        onMouseLeave={() => setHoveredSection(null)}
        onClick={() =>
          setActiveSection(activeSection === "networking" ? null : "networking")
        }
        className="cursor-pointer"
      />
    );

    // Create the visible pulsing line
    networkingLines.push(
      <motion.line
        key={`net-line-${i}`}
        x1={isoX(line.startX, line.startY)}
        y1={isoY(line.startX, line.startY)}
        x2={isoX(line.endX, line.endY)}
        y2={isoY(line.endX, line.endY)}
        stroke={
          hoveredSection === "networking" || activeSection === "networking"
            ? nvidiaColors.darkGreen
            : nvidiaColors.green
        }
        strokeWidth={
          hoveredSection === "networking" || activeSection === "networking"
            ? "2.5"
            : "1.5"
        }
        strokeDasharray={line.dashArray}
        animate={{
          strokeOpacity: [0.3, 0.9, 0.3],
          strokeDashoffset: [0, -12],
        }}
        transition={{
          duration: 2,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
          delay: line.delay,
        }}
        pointerEvents="none" // Disable pointer events on the visible line
      />
    );
  });

  // Update the createExtrudedPolygon function to handle pause/resume
  const createExtrudedPolygon = (section, isHovered) => {
    // For networking section which is handled differently
    if (section.type === "edge") {
      return null;
    }

    // Parse the points string into an array of coordinates
    const pointsArray = section.points.split(" ").map((point) => {
      const [x, y] = point.split(",").map(Number.parseFloat);
      return { x, y };
    });

    // Get the fill and stroke colors based on hover state
    const fillColor = isHovered
      ? section.hoverColor || section.color
      : section.color;
    const strokeColor = isHovered
      ? section.hoverColor || section.color
      : section.color;

    // Create the base polygon (bottom face)
    const basePolygon = (
      <polygon
        points={section.points}
        fill={fillColor}
        fillOpacity={isHovered ? 0.4 : 0.2}
        stroke={strokeColor}
        strokeWidth={isHovered ? 2 : 1}
        onMouseEnter={() => {
          setHoveredSection(section.id);
          setIsPaused(true);
          setActiveSection(null); // Clear active section when hovering
        }}
        onMouseLeave={() => {
          setHoveredSection(null);
          setIsPaused(false);
        }}
        onClick={() =>
          setActiveSection(activeSection === section.id ? null : section.id)
        }
        className="transition-all duration-300 cursor-pointer"
      />
    );

    // If not hovered and not the active section, just return the base polygon
    if (!isHovered && section.id !== activeSection) {
      return basePolygon;
    }

    // For hovered state or active section, create extruded elements with animation
    const extrusionHeight = 15 * scale;

    // Create the top face (extruded upward)
    const topPoints = pointsArray
      .map((p) => `${p.x},${p.y - extrusionHeight}`)
      .join(" ");

    // Create side faces connecting base to top
    const sideFaces: React.ReactNode[] = [];
    for (let i = 0; i < pointsArray.length; i++) {
      const next = (i + 1) % pointsArray.length;
      const sidePoints = `
        ${pointsArray[i].x},${pointsArray[i].y} 
        ${pointsArray[i].x},${pointsArray[i].y - extrusionHeight} 
        ${pointsArray[next].x},${pointsArray[next].y - extrusionHeight} 
        ${pointsArray[next].x},${pointsArray[next].y}
      `;

      sideFaces.push(
        <motion.polygon
          key={`side-${i}`}
          points={sidePoints}
          fill={fillColor}
          fillOpacity={0.4}
          stroke={strokeColor}
          strokeWidth={1}
          className="pointer-events-none"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: extrusionHeight }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      );
    }

    // Return the complete extruded section with animation
    return (
      <g>
        {basePolygon}
        <AnimatePresence>
          {(isHovered || (!isPaused && section.id === activeSection)) && (
            <>
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {sideFaces}
                <motion.polygon
                  points={topPoints}
                  fill={fillColor}
                  fillOpacity={0.6}
                  stroke={strokeColor}
                  strokeWidth={2}
                  className="pointer-events-none"
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              </motion.g>
            </>
          )}
        </AnimatePresence>
      </g>
    );
  };

  // Handle section hover
  const handleSectionHover = (sectionId: string | null) => {
    setHoveredSection(sectionId);
  };

  // Handle section click
  const handleSectionClick = (sectionId: string) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  // Create section info panel
  const renderSectionInfo = () => {
    const currentSection = activeSection || hoveredSection;
    if (!currentSection || !isSectionInfoEnabled) return null;

    const section = sections.find((s) => s.id === currentSection);
    if (!section) return null;

    let description = "";
    let features: string[] = [];

    switch (section.id) {
      case "compute":
        description =
          "High-performance GPU compute resources for AI/ML workloads";
        features = [
          "NVIDIA A100/H100 GPUs",
          "Optimized for deep learning",
          "Auto-scaling compute clusters",
          "Pre-configured CUDA environments",
        ];
        break;
      case "containers":
        description =
          "Pre-built container images with all dependencies installed";
        features = [
          "PyTorch, TensorFlow, JAX containers",
          "Custom Docker images",
          "Container orchestration",
          "Version-controlled environments",
        ];
        break;
      case "git":
        description = "Version-controlled code repositories and resources";
        features = [
          "Git integration",
          "CI/CD pipelines",
          "Code versioning",
          "Collaborative development",
        ];
        break;
      case "networking":
        description = "High-speed networking for efficient data transfer";
        features = [
          "100 Gbps networking",
          "Low-latency connections",
          "Secure VPC configuration",
          "Global edge network",
        ];
        break;
    }

    return (
      <div
        className="absolute bottom-2 right-2 bg-gray-900 text-white p-2 rounded-lg shadow-lg"
        style={{
          borderLeft: `4px solid ${section.color}`,
          maxWidth: "200px",
          fontSize: "12px",
        }}
      >
        <h3 className="font-bold" style={{ color: section.color }}>
          {section.name}
        </h3>
        <p className="text-gray-300 mt-1 text-xs">{description}</p>
        <ul className="mt-2 space-y-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center text-xs">
              <span className="mr-1.5" style={{ color: section.color }}>
                •
              </span>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderOverlays = () => {
    return (
      <g>
        <AnimatePresence>
          {sections
            .filter(
              (section) =>
                section.type !== "edge" &&
                (hoveredSection === section.id || activeSection === section.id)
            )
            .map((section) => {
              if (!section.overlay) return null;

              // Parse the points string into an array of coordinates
              const pointsArray = section.points.split(" ").map((point) => {
                const [x, y] = point.split(",").map(Number.parseFloat);
                return { x, y };
              });

              // Calculate center point and dimensions for SVG positioning
              const minX = Math.min(...pointsArray.map((p) => p.x));
              const maxX = Math.max(...pointsArray.map((p) => p.x));
              const minY =
                Math.min(...pointsArray.map((p) => p.y)) - 15 * scale; // Account for extrusion
              const maxY =
                Math.max(...pointsArray.map((p) => p.y)) - 15 * scale;

              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              const width = maxX - minX;
              const height = maxY - minY;

              const overlay = section.overlay;
              const overlayWidth = width * (overlay.width || 0.8);
              const overlayHeight = height * (overlay.height || 0.8);
              const offsetX = overlay.offsetX || 0;
              const offsetY = overlay.offsetY || 0;
              const additionalScale = overlay.scale || 1;
              const additionalRotation = overlay.rotation || 0;

              if (overlay.isForeignObject) {
                return (
                  <motion.foreignObject
                    key={`overlay-${section.id}`}
                    x={centerX - overlayWidth / 2 + offsetX}
                    y={centerY - overlayHeight / 2 + offsetY - 30} // Adjust position for better alignment
                    width={overlayWidth}
                    height={overlayHeight}
                    className="pointer-events-none"
                    overflow="visible"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeOut",
                      scale: { type: "spring", stiffness: 300, damping: 25 },
                    }}
                  >
                    <Chip
                      selected={true}
                      chipName="H100"
                      manufacturerName="NVIDIA"
                      glowColors={{
                        selected: "#76B900", // NVIDIA green
                        deselected: "#FFFFFF",
                      }}
                      className={classNames("h-[70px]")}
                      onClick={() => {}}
                      style={{
                        transform: `rotate(${
                          63 + additionalRotation
                        }deg) skewY(-35deg) scale(${additionalScale})`,
                        transformOrigin: "10% 170%",
                      }}
                    />
                  </motion.foreignObject>
                );
              } else {
                return (
                  <motion.g
                    key={`overlay-${section.id}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeOut",
                      scale: { type: "spring", stiffness: 300, damping: 25 },
                    }}
                  >
                    <svg
                      x={centerX - overlayWidth / 2 + offsetX}
                      y={centerY - overlayHeight / 2 + offsetY}
                      width={overlayWidth}
                      height={overlayHeight}
                      viewBox="0 0 512 512"
                      overflow="visible"
                      preserveAspectRatio="xMidYMid meet"
                      className="pointer-events-none"
                    >
                      {/* Apply isometric transformation to align with the top face */}
                      <g
                        transform={`
                rotate(${63 + additionalRotation}, 256, 256) 
                
                skewY(-35)
                scale(${additionalScale})
              `}
                      >
                        {section.overlay.component}
                      </g>
                    </svg>
                  </motion.g>
                );
              }
            })}
        </AnimatePresence>
      </g>
    );
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg ref={svgRef} width={width} height={height}>
          {/* Base grid */}
          {gridLines}

          {/* Networking pulse lines with hover capability */}
          <g>{networkingLines}</g>

          {/* Section polygons with extrusion effect */}
          {sections
            .filter((section) => section.type !== "edge")
            .map((section) => {
              const isHovered =
                hoveredSection === section.id || activeSection === section.id;
              return (
                <g key={section.id}>
                  {createExtrudedPolygon(section, isHovered)}
                </g>
              );
            })}
          {/* Overlay elements */}
          {renderOverlays()}
        </svg>

        {/* Section info panel */}
        {renderSectionInfo()}
      </div>
    </div>
  );
};

export default LaunchableInteractive;
